/**
 * Date Configuration
 *
 * Centralized configuration for date formatting and timezone handling.
 */

import { enGB } from 'date-fns/locale';
import type { Locale } from 'date-fns';

// =============================================================================
// Default Configuration
// =============================================================================

/**
 * Default locale for date formatting
 * Using British English (DD/MM/YYYY) as the standard
 */
export const DEFAULT_LOCALE: Locale = enGB;

/**
 * Default locale string for Intl APIs
 */
export const DEFAULT_LOCALE_STRING = 'en-GB';

/**
 * Default fallback when date is invalid
 */
export const DEFAULT_FALLBACK = '-';

/**
 * Default date-fns format strings
 */
export const DATE_FORMATS = {
  /** Short date: 07-01-2026 */
  short: 'dd-MM-yyyy',

  /** Medium date: Jan 07, 2026 */
  medium: 'MMM dd, yyyy',

  /** Long date: January 7, 2026 */
  long: 'MMMM d, yyyy',

  /** Date with time: 07-01-2026, 02:30 PM */
  dateTime: 'dd-MM-yyyy, hh:mm a',

  /** Time only: 14:30 */
  time: 'HH:mm',

  /** Time with seconds: 14:30:45 */
  timeWithSeconds: 'HH:mm:ss',

  /** ISO date: 2026-01-07 */
  iso: 'yyyy-MM-dd',

  /** Dashed date: 04-04-2024 (DD-MM-YYYY), e.g. for tables/BBD */
  dashed: 'dd-MM-yyyy',

  /** Month and year: January 2026 */
  monthYear: 'MMMM yyyy',

  /** Day with weekday: Monday, January 7, 2026 */
  withWeekday: 'EEEE, MMMM d, yyyy',

  /** Full date and time: Monday, January 7, 2026 at 14:30 */
  full: "EEEE, MMMM d, yyyy 'at' HH:mm",

  /** Journey date and time: Apr 23, 2025 • 10:00 PM (track & trace timeline) */
  journeyDateTime: "MMM d, yyyy  h:mm a",
} as const;

// =============================================================================
// Timezone Configuration
// =============================================================================

/**
 * Get the user's browser timezone
 * This is detected at runtime from the browser
 */
export function getUserTimezone(): string {
  if (typeof window === 'undefined') {
    // Server-side: default to UTC
    return 'UTC';
  }

  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Common timezone presets for logistics operations
 */
export const TIMEZONE_PRESETS = {
  // Europe
  amsterdam: 'Europe/Amsterdam',
  london: 'Europe/London',
  berlin: 'Europe/Berlin',
  paris: 'Europe/Paris',

  // Americas
  newYork: 'America/New_York',
  losAngeles: 'America/Los_Angeles',
  chicago: 'America/Chicago',

  // Asia Pacific
  shanghai: 'Asia/Shanghai',
  tokyo: 'Asia/Tokyo',
  singapore: 'Asia/Singapore',
  hongKong: 'Asia/Hong_Kong',
  sydney: 'Australia/Sydney',
  melbourne: 'Australia/Melbourne',

  // Universal
  utc: 'UTC',
} as const;

// =============================================================================
// Locale Map
// =============================================================================

/**
 * Import additional locales as needed
 * Add more locales here when the app expands to new regions
 */
export const SUPPORTED_LOCALES = {
  'en-GB': enGB,
  // Add more as needed:
  // 'nl': nl,
  // 'de': de,
  // 'fr': fr,
  // 'zh-CN': zhCN,
} as const;

/**
 * Get a locale object by string identifier
 */
export function getLocale(localeString: string): Locale {
  return (SUPPORTED_LOCALES as Record<string, Locale>)[localeString] || DEFAULT_LOCALE;
}

